/**
 * Card-payment slot holds: reserve DB segments before Stripe charge so a
 * concurrent booker cannot pay for an already-taken professional slot.
 */
import { supabase } from '../config/supabase'
import {
  createAppointment,
  createMultiDayAppointments,
  cancelAppointment,
  cancelAppointmentsByGroupBookingId,
  updateAppointmentStatus,
  updateAppointmentPayment,
} from '../services/appointmentService'

export const PAYMENT_HOLD_TAG = '[PAYMENT_HOLD]'

export const stripPaymentHoldTag = (notes) =>
  String(notes || '')
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line && line !== PAYMENT_HOLD_TAG)
    .join('\n')

export const releasePaymentHold = async (hold) => {
  if (!hold) return
  try {
    if (hold.groupBookingId) {
      await cancelAppointmentsByGroupBookingId(hold.groupBookingId)
      return
    }
    const ids = Array.isArray(hold.holdIds) ? hold.holdIds.filter(Boolean) : []
    for (const id of ids) {
      await cancelAppointment(id)
    }
  } catch (err) {
    console.warn('Failed to release payment hold:', err?.message || err)
  }
}

export const releaseStalePaymentHoldsForUser = async (userId) => {
  if (!userId) return
  try {
    const { data: rows, error } = await supabase
      .from('appointments')
      .select('id, group_booking_id')
      .eq('user_id', userId)
      .eq('status', 'pending')
      .ilike('notes', `%${PAYMENT_HOLD_TAG}%`)

    if (error || !rows?.length) return

    const seenGroups = new Set()
    for (const row of rows) {
      if (row.group_booking_id) {
        if (seenGroups.has(row.group_booking_id)) continue
        seenGroups.add(row.group_booking_id)
        await cancelAppointmentsByGroupBookingId(row.group_booking_id)
      } else if (row.id) {
        await cancelAppointment(row.id)
      }
    }
  } catch (err) {
    console.warn('Failed clearing stale payment holds:', err?.message || err)
  }
}

export const createPaymentHoldFromPending = async (pending) => {
  if (!pending) {
    return { success: false, error: 'Missing booking data for payment hold' }
  }

  await releaseStalePaymentHoldsForUser(pending.userId)

  const holdNotes = `${pending.notes || ''}\n${PAYMENT_HOLD_TAG}`.trim()
  const holdPayload = {
    ...pending,
    status: 'pending',
    paymentStatus: 'unpaid',
    paymentMethod: 'card',
    notes: holdNotes,
  }

  const result = holdPayload.isMultiDay
    ? await createMultiDayAppointments(holdPayload)
    : await createAppointment(holdPayload)

  if (!result?.success) {
    return {
      success: false,
      error: result?.error || 'This time slot is no longer available.',
    }
  }

  const holdIds = holdPayload.isMultiDay
    ? (result.allData || []).map((row) => row.id).filter(Boolean)
    : [result.data?.id].filter(Boolean)

  if (!holdIds.length) {
    return { success: false, error: 'Failed to reserve time slot. Please try again.' }
  }

  return {
    success: true,
    hold: {
      holdIds,
      primaryId: result.data?.id || holdIds[0],
      groupBookingId: result.groupBookingId || null,
    },
  }
}

export const confirmPaymentHold = async (hold, paymentIntentId, notes = null) => {
  if (!hold?.holdIds?.length) {
    return { success: false, error: 'Missing reserved booking to confirm after payment.' }
  }

  const cleanedNotes = stripPaymentHoldTag(notes)
  const confirmedRows = []

  for (const id of hold.holdIds) {
    const statusResult = await updateAppointmentStatus(id, 'confirmed')
    if (!statusResult?.success) {
      return { success: false, error: statusResult?.error || 'Failed to confirm reserved booking' }
    }

    const payResult = await updateAppointmentPayment(id, {
      payment_status: 'paid',
      payment_method: 'card',
      payment_intent_id: paymentIntentId || null,
    })
    if (!payResult?.success) {
      return { success: false, error: payResult?.error || 'Failed to mark reserved booking as paid' }
    }

    if (cleanedNotes) {
      try {
        await supabase
          .from('appointments')
          .update({ notes: cleanedNotes })
          .eq('id', id)
      } catch (_) {
        // non-fatal
      }
    }

    confirmedRows.push(payResult.data || statusResult.data || { id })
  }

  return {
    success: true,
    data: confirmedRows[0] || { id: hold.primaryId || hold.holdIds[0] },
    allData: confirmedRows,
    groupBookingId: hold.groupBookingId || null,
  }
}
